<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { flowsAPI } from 'dashboard/api/flows';
import FlowBuilder from 'dashboard/components/FlowBuilder/FlowBuilder.vue';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const flows = ref([]);
const isLoading = ref(false);
const pendingDeleteId = ref(null);
const activeFlowId = ref(
  route.params.flowId ? Number(route.params.flowId) : null
);
const isCreating = ref(false);

async function loadFlows() {
  isLoading.value = true;
  try {
    const { data } = await flowsAPI.list();
    flows.value = data;
  } finally {
    isLoading.value = false;
  }
}

function editFlow(id) {
  activeFlowId.value = id;
  isCreating.value = false;
  router.replace({ name: 'flows_edit', params: { flowId: id } });
}

function newFlow() {
  activeFlowId.value = null;
  isCreating.value = true;
  router.replace({ name: 'flows_index' });
}

function backToList() {
  activeFlowId.value = null;
  isCreating.value = false;
  router.replace({ name: 'flows_index' });
  loadFlows();
}

function confirmDelete(id) {
  pendingDeleteId.value = id;
}

async function doDelete() {
  const id = pendingDeleteId.value;
  pendingDeleteId.value = null;
  await flowsAPI.delete(id);
  flows.value = flows.value.filter(f => f.id !== id);
  if (activeFlowId.value === id) backToList();
}

onMounted(loadFlows);
</script>

<template>
  <div class="flex h-full overflow-hidden">
    <!-- Delete confirmation modal -->
    <div
      v-if="pendingDeleteId"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="pendingDeleteId = null"
    >
      <div class="bg-white rounded-xl shadow-xl p-6 w-80">
        <p class="text-sm font-semibold text-gray-800 mb-4">
          {{ t('MINISTRY.FLOW_BUILDER.DELETE_CONFIRM') }}
        </p>
        <div class="flex justify-end gap-2">
          <button
            class="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            @click="pendingDeleteId = null"
          >
            {{ t('MINISTRY.FLOW_BUILDER.CANCEL') }}
          </button>
          <button
            class="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600"
            @click="doDelete"
          >
            {{ t('MINISTRY.FLOW_BUILDER.DELETE') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Sidebar list -->
    <div
      class="w-64 shrink-0 border-r border-n-weak bg-n-solid-1 flex flex-col"
    >
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
      >
        <h2 class="text-sm font-semibold text-n-slate-12">
          {{ t('MINISTRY.FLOW_BUILDER.FLOWS_TITLE') }}
        </h2>
        <button
          class="flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-woot-500 text-white hover:bg-woot-600"
          @click="newFlow"
        >
          <span class="i-lucide-plus size-3" />
          {{ t('MINISTRY.FLOW_BUILDER.NEW') }}
        </button>
      </div>

      <div class="flex-1 overflow-y-auto py-2">
        <div v-if="isLoading" class="flex justify-center py-8">
          <span class="i-lucide-loader-2 size-5 animate-spin text-woot-500" />
        </div>
        <div
          v-else-if="flows.length === 0"
          class="px-4 py-6 text-center text-sm text-n-slate-8"
        >
          {{ t('MINISTRY.FLOW_BUILDER.NO_FLOWS') }}
        </div>
        <button
          v-for="flow in flows"
          :key="flow.id"
          class="w-full text-left px-4 py-2.5 flex items-center gap-2 hover:bg-n-slate-2 group"
          :class="
            activeFlowId === flow.id && !isCreating
              ? 'bg-woot-50 text-woot-600'
              : 'text-n-slate-11'
          "
          @click="editFlow(flow.id)"
        >
          <span class="i-lucide-git-branch size-3.5 shrink-0" />
          <span class="flex-1 text-sm truncate">{{ flow.name }}</span>
          <span
            class="shrink-0 w-1.5 h-1.5 rounded-full"
            :class="flow.is_active ? 'bg-green-400' : 'bg-n-slate-5'"
          />
          <button
            class="shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-red-500 transition-opacity"
            @click.stop="confirmDelete(flow.id)"
          >
            <span class="i-lucide-trash-2 size-3" />
          </button>
        </button>
      </div>
    </div>

    <!-- Builder / empty state -->
    <div class="flex-1 overflow-hidden">
      <FlowBuilder
        v-if="activeFlowId || isCreating"
        :key="activeFlowId ?? 'new'"
        :flow-id="activeFlowId"
      />
      <div
        v-else
        class="flex flex-col items-center justify-center h-full text-n-slate-8 gap-3"
      >
        <span class="i-lucide-git-branch size-10" />
        <p class="text-sm">{{ t('MINISTRY.FLOW_BUILDER.SELECT_FLOW_HINT') }}</p>
        <button
          class="px-3 py-1.5 text-sm rounded-md bg-woot-500 text-white hover:bg-woot-600"
          @click="newFlow"
        >
          {{ t('MINISTRY.FLOW_BUILDER.NEW_FLOW') }}
        </button>
      </div>
    </div>
  </div>
</template>
